import React, { FC, useCallback } from 'react';
import { Form } from 'react-final-form';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { clearFields, patchOffer } from 'src/inbox/ducks/offer';
import { close } from 'src/common/ducks/zippy';
import { plural } from 'src/common/helpers';
import { END_DATE_FIELD,
  FORMS,
  PREFERRED_DATE_FIELD } from 'src/inbox/constants';
import { ordinalSuffix } from 'src/inbox/helpers';
import FormError from 'src/common/components/FormError';
import FormButtons from 'src/common/components/FormButtons';
import type { ImmutableTrip } from 'src/types/trips/Trips';
import dateTimeDurationSelector from 'src/inbox/selectors/dateTimeDuration/dateTimeDuration';
import type { Offer } from 'src/types/offer/Offer';
import { newTZDate } from 'src/common/utils/dateHelpers';
import { isBefore } from 'date-fns';
import DateTimeSnippet from 'src/common/components/DateTimeSnippet';
import DateTime from 'src/inbox/components/DateTime';
import DatesFormDatePicker from 'src/inbox/components/cards/Dates/DatesFormDatePicker';
import CUSTOM_DATES_INDEX from 'src/inbox/components/cards/Dates/constants';
import s from './DatesForm.module.scss';

export type DateFormValues = {
  /** Expect -1 when custom dates are input */
  suggested_date_index: number;
  preferred_date?: Offer['preferred_date'];
  end_date?: Offer['end_date'];
};

type DatesFormProps = {
  firstName: string;
  isRenter: boolean;
  /** The only caller is src/inbox/components/pages/BookNow/index.tsx */
  withBookingMotivators?: boolean;
  trip: ImmutableTrip;
};

export const validate = (values: DateFormValues): Partial<Record<keyof DateFormValues, string>> => {
  if (values.suggested_date_index === CUSTOM_DATES_INDEX) {
    if (!values.preferred_date) {
      return { preferred_date: 'Please pick a depart date.' };
    }
    if (values.preferred_date && !values.end_date) {
      return { end_date: 'Please pick a return date.' };
    }
    if (values.end_date
        && isBefore(newTZDate(values.end_date), newTZDate(values.preferred_date))
    ) {
      return { end_date: 'End date must be after start date.' };
    }
  }
  return {};
};

export const BareDatesForm: FC<DatesFormProps> = ({
  firstName,
  isRenter,
  trip,
  withBookingMotivators,
}) => {
  const {
    offerTrip: {
      suggestedDatesWithEnds,
      isOfferDatesModified,
      startDate,
      endDate,
    },
  } = useSelector(dateTimeDurationSelector(trip.get('pk')));

  const preferred = isRenter ? 'offer' : 'preferred';

  const dispatch = useDispatch();
  const closeModal = useCallback(() => dispatch(close(FORMS.DATES)), [dispatch]);

  const suggestedDateIndex = suggestedDatesWithEnds.findIndex((suggested) => (
    suggested.preferred_date === startDate && suggested.end_date === endDate
  ));

  return (
    <Form<DateFormValues>
      initialValues={suggestedDateIndex === CUSTOM_DATES_INDEX ? {
        preferred_date: startDate,
        end_date: endDate,
        suggested_date_index: CUSTOM_DATES_INDEX,
      } : {
        suggested_date_index: suggestedDateIndex,
      }}
      onSubmit={(values) => {
        dispatch(patchOffer(
          values.suggested_date_index === CUSTOM_DATES_INDEX
            ? {
              preferred_date: values.preferred_date,
              end_date: values.end_date,
            }
            : {
              preferred_date: suggestedDatesWithEnds[values.suggested_date_index].preferred_date,
              end_date: suggestedDatesWithEnds[values.suggested_date_index].end_date,
            },
        ));
        closeModal();
      }}
      validate={validate}
    >
      {({
        handleSubmit,
        values,
        error,
        dirty,
        form: {
          change,
          reset,
        },
        submitting,
      }) => (
        <form
          method="POST"
          onSubmit={handleSubmit}
          className={s.root}
        >
          <h3 className={s.title}>
            {isRenter ? 'Edit Trip Date' : 'Select Offer Date'}
          </h3>
          <p className={s.message}>
            {isRenter
              ? 'Request a change to your trip date.'
              : `${firstName} provided ${suggestedDatesWithEnds.length} option${plural(suggestedDatesWithEnds.length)} for the requested trip date.`}
          </p>
          <FormError error={error} />
          <div className={s.inner}>
            <button
              className={s.clearAll}
              type="button"
              disabled={!(dirty || isOfferDatesModified)}
              onClick={() => {
                reset();
                dispatch(clearFields([
                  PREFERRED_DATE_FIELD,
                  END_DATE_FIELD,
                ]));
              }}
            >
              Clear changes
            </button>
            {suggestedDatesWithEnds.map((suggested, index) => (
              <div className={s.row} key={JSON.stringify(suggested)}>
                <button
                  type="button"
                  aria-pressed={values.suggested_date_index === index}
                  aria-label={`Select preset date ${index}: depart on ${suggested.preferred_date}, and return on ${suggested.end_date}`}
                  onClick={() => change('suggested_date_index', index)}
                  className={s.radioButton}
                  id={JSON.stringify(suggested)}
                />
                <label htmlFor={JSON.stringify(suggested)}>
                  <DateTimeSnippet>
                    <DateTime
                      departHeading="Depart on"
                      title={ordinalSuffix`${firstName}’s ${index} ${(index && index > 0) ? 'alternative' : preferred} date:`}
                      departDateTime={moment(suggested.preferred_date)}
                      returnDateTime={moment(suggested.end_date)}
                    />
                  </DateTimeSnippet>
                </label>
              </div>
            ))}
            <div className={s.row}>
              <button
                type="button"
                aria-pressed={values.suggested_date_index === CUSTOM_DATES_INDEX}
                aria-label="Open date range picker to select custom date"
                onClick={() => change('suggested_date_index', CUSTOM_DATES_INDEX)}
                className={s.radioButton}
              />
              <DateTimeSnippet>
                <DateTime
                  departHeading="Depart on"
                  title={withBookingMotivators ? 'New requested date:' : 'Your suggested date:'}
                  departDateTime={(
                    <DatesFormDatePicker
                      fieldName="preferred_date"
                      placeholder="Depart date"
                      withBookingMotivators={withBookingMotivators}
                    />
                  )}
                  returnDateTime={(
                    <DatesFormDatePicker
                      fieldName="end_date"
                      placeholder="Return date"
                      withBookingMotivators={withBookingMotivators}
                    />
                  )}
                />
              </DateTimeSnippet>
            </div>
          </div>
          <FormButtons
            classNameModifier="inbox"
            submitting={submitting}
            closeModal={closeModal}
            onSubmit={handleSubmit}
          />
        </form>
      )}
    </Form>
  );
};

export default BareDatesForm;
